import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ACCOUNT_TYPE_FILTERS } from '@app/shared';
import { Box, IconButton, Menu, MenuItem, TextField } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENROLLMENT_SOURCES } from '@app/shared';
import type { EnrollmentListItemDto, EnrollmentStatus } from '@app/shared';
import { AgentChainSelect, deepestAgent } from '../../components/AgentChainSelect';
import type { AgentChain } from '../../components/AgentChainSelect';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { ConfirmDialog } from '../../components/dialogs';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { api } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import {
  AddressChecksDialog,
  ApiLogDialog,
  DetailsDialog,
  LifecycleDialog,
} from './EnrollmentDialogs';
import type { DialogTarget } from './EnrollmentDialogs';
import { useEnrollments } from './api';
import { currentStep, sourceLabel, stepLabel, stepPath } from './labels';

export type EnrollmentListMode = 'all' | 'completed' | 'incomplete';

type Row = EnrollmentListItemDto;
type ColumnKey =
  | 'enrollmentId'
  | 'name'
  | 'contactNumber'
  | 'email'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'
  | 'planName'
  | 'planPrice'
  | 'phoneCost'
  | 'amountPaid'
  | 'paymentMode'
  | 'createdAt'
  | 'sim'
  | 'mdn'
  | 'step'
  | 'status'
  | 'incompleteReason'
  | 'assignmentStatus'
  | 'source';

/**
 * Every data column, with the reference's header. `enrollmentId` and `createdAt` are also the
 * fields the API sorts on. The reference's "Authorization code" and "DOB" columns are left out:
 * nothing in this application fills them. "Phone cost" is behind its release switch.
 */
const COLUMNS: Record<ColumnKey, Column<Row>> = {
  enrollmentId: {
    key: 'enrollmentId',
    header: 'Enrollment id',
    sortable: true,
    nowrap: true,
    render: (r) => r.enrollmentId,
  },
  name: { key: 'name', header: 'Name', render: (r) => r.name },
  contactNumber: { key: 'contactNumber', header: 'Contact number', render: (r) => r.contactNumber },
  email: { key: 'email', header: 'Email', render: (r) => r.email },
  address: { key: 'address', header: 'Address', render: (r) => r.address },
  city: { key: 'city', header: 'City', render: (r) => r.city },
  state: { key: 'state', header: 'State', render: (r) => r.state },
  zip: { key: 'zip', header: 'Zip', render: (r) => r.zip },
  planName: { key: 'planName', header: 'Plan name', render: (r) => r.planName },
  planPrice: {
    key: 'planPrice',
    header: 'Plan price',
    align: 'right',
    render: (r) => formatCents(r.planPriceCents),
  },
  phoneCost: {
    key: 'phoneCost',
    header: 'Phone cost',
    align: 'right',
    render: (r) => formatCents(r.phoneCostCents),
  },
  amountPaid: {
    key: 'amountPaid',
    header: 'Amount paid by customer',
    align: 'right',
    render: (r) => formatCents(r.amountPaidCents),
  },
  paymentMode: { key: 'paymentMode', header: 'Payment mode', render: (r) => r.paymentMode },
  createdAt: {
    key: 'createdAt',
    header: 'Enrollment date',
    sortable: true,
    nowrap: true,
    render: (r) => formatDateTime(r.enrollmentDate),
  },
  sim: { key: 'sim', header: 'SIM', nowrap: true, render: (r) => r.sim },
  mdn: { key: 'mdn', header: 'MDN', render: (r) => r.mdn },
  step: { key: 'step', header: 'Enrollment step', nowrap: true, render: (r) => stepLabel(r.step) },
  status: {
    key: 'status',
    header: 'Enrollment status',
    render: (r) => <StatusChip status={r.status} />,
  },
  incompleteReason: {
    key: 'incompleteReason',
    header: 'Incomplete reason',
    render: (r) => r.incompleteReason,
  },
  assignmentStatus: {
    key: 'assignmentStatus',
    header: 'SIM/Device assignment status',
    render: (r) => r.assignmentStatus,
  },
  source: { key: 'source', header: 'Source', render: (r) => sourceLabel(r.source) },
};

interface ModeConfig {
  title: string;
  code: string;
  /** The status this list is limited to; "All enrollments" lets the user choose. */
  status: EnrollmentStatus | '';
  columns: ColumnKey[];
}

const MODES: Record<EnrollmentListMode, ModeConfig> = {
  // The reference's column list for "All enrollments" was not read. It shows both kinds of
  // order, so it gets the columns of both lists: the incomplete list's, with the completed
  // list's payment, SIM, MDN and assignment columns placed where that list has them.
  all: {
    title: 'All enrollments',
    code: 'PC1170',
    status: '',
    columns: [
      'enrollmentId',
      'name',
      'contactNumber',
      'email',
      'address',
      'city',
      'state',
      'zip',
      'planName',
      'planPrice',
      'amountPaid',
      'paymentMode',
      'createdAt',
      'sim',
      'mdn',
      'step',
      'status',
      'incompleteReason',
      'assignmentStatus',
      'source',
    ],
  },
  completed: {
    title: 'List of completed non-lifeline enrollments',
    code: 'PC1171',
    status: 'Complete',
    columns: [
      'enrollmentId',
      'name',
      'address',
      'city',
      'state',
      'zip',
      'planName',
      'planPrice',
      'phoneCost',
      'amountPaid',
      'paymentMode',
      'createdAt',
      'sim',
      'mdn',
      'status',
      'assignmentStatus',
      'source',
    ],
  },
  incomplete: {
    title: 'Incomplete enrollments',
    code: 'PC1172',
    status: 'Incomplete',
    columns: [
      'enrollmentId',
      'name',
      'contactNumber',
      'email',
      'address',
      'city',
      'state',
      'zip',
      'planName',
      'createdAt',
      'step',
      'status',
      'incompleteReason',
      'source',
    ],
  },
};

/** "Enrollment status" choices on All enrollments, with the permission of the list each one reads. */
const STATUS_CHOICES: Array<{ value: EnrollmentStatus; label: string; code: string }> = [
  { value: 'Complete', label: 'Complete enrollments', code: 'PC1171' },
  { value: 'Incomplete', label: 'Incomplete enrollments', code: 'PC1172' },
];

const NO_FILTERS = {
  dateFrom: '',
  dateTo: '',
  q: '',
  source: '',
  status: '',
  agentId: '',
  accountType: '',
};
const DATE_FIELD = { slotProps: { inputLabel: { shrink: true } } } as const;

/**
 * The three enrollment lists (PC1170, PC1171, PC1172): one screen, the mode decides the title,
 * the status and the columns.
 */
export function EnrollmentListPage({ mode }: { mode: EnrollmentListMode }) {
  const config = MODES[mode];
  const navigate = useNavigate();
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  const list = useListState(NO_FILTERS, 10, '-createdAt');
  // The selects of the agent filter; the deepest one chosen is what the list is filtered by.
  const [chain, setChain] = useState<AgentChain>({});
  const enrollments = useEnrollments({
    ...list.params,
    status: config.status || list.applied.status,
  });

  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: Row } | null>(null);
  const [details, setDetails] = useState<DialogTarget | null>(null);
  const [lifecycle, setLifecycle] = useState<DialogTarget | null>(null);
  const [apiLog, setApiLog] = useState<DialogTarget | null>(null);
  const [checks, setChecks] = useState<DialogTarget | null>(null);
  const [reminding, setReminding] = useState<DialogTarget | null>(null);
  const notify = useNotify();
  const optionsOn = featureOn('enrollment-row-options');
  const remind = useMutation({
    mutationFn: (target: DialogTarget) =>
      api.post<{ message: string }>(`/enrollments/${target.id}/reminder`, {}),
    onSuccess: (r) => {
      notify.success(r.message);
      setReminding(null);
    },
    onError: (err) => notify.error(err),
  });

  const pick = (open: (target: DialogTarget) => void) => {
    if (menu) open({ id: menu.row.id, enrollmentId: menu.row.enrollmentId });
    setMenu(null);
  };

  const columns: Column<Row>[] = [
    {
      key: '#',
      header: '#',
      width: 50,
      render: (_r, i) => rowNumber(i, list.page, list.pageSize),
    },
    {
      key: 'option',
      header: 'Option',
      width: 60,
      render: (r) => (
        <IconButton
          size="small"
          aria-label={`Options for ${r.enrollmentId}`}
          aria-haspopup="menu"
          onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
    ...config.columns
      .filter((key) => key !== 'phoneCost' || featureOn('enrollment-phone-cost'))
      .map((key) => COLUMNS[key]),
  ];

  const statusChoices = STATUS_CHOICES.filter((s) => can(s.code));

  return (
    <Box>
      <PageHeader title={config.title} code={config.code} />

      <FilterPanel
        onSearch={list.search}
        onReset={() => {
          setChain({});
          list.reset();
        }}
      >
        <TextField
          type="date"
          label="Date range: From"
          value={list.draft.dateFrom}
          onChange={(e) => list.setField('dateFrom', e.target.value)}
          {...DATE_FIELD}
        />
        <TextField
          type="date"
          label="Date range: To"
          value={list.draft.dateTo}
          onChange={(e) => list.setField('dateTo', e.target.value)}
          {...DATE_FIELD}
        />
        <TextField
          label="Search"
          placeholder="Search by Customer First Name, Last Name, Enrollment ID, Phone or Email"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
          slotProps={{ htmlInput: { maxLength: 60 }, inputLabel: { shrink: true } }}
          sx={{ gridColumn: { md: 'span 2' } }}
        />
        <TextField
          select
          label="Source"
          value={list.draft.source}
          onChange={(e) => list.setField('source', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {ENROLLMENT_SOURCES.map((s) => (
            <MenuItem key={s} value={s}>
              {sourceLabel(s)}
            </MenuItem>
          ))}
        </TextField>
        {featureOn('account-type-filters') ? (
          <TextField
            select
            label="Account Type"
            value={list.draft.accountType}
            onChange={(e) => list.setField('accountType', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {ACCOUNT_TYPE_FILTERS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <AgentChainSelect
          value={chain}
          onChange={(next) => {
            setChain(next);
            list.setField('agentId', deepestAgent(next) ?? '');
          }}
        />
        {mode === 'all' ? (
          <TextField
            select
            label="Enrollment status"
            value={list.draft.status}
            onChange={(e) => list.setField('status', e.target.value)}
          >
            <MenuItem value="">All enrollments</MenuItem>
            {/* Each status is its own list with its own permission. */}
            {statusChoices.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </FilterPanel>

      <DataTable
        label={config.title}
        columns={columns}
        rows={enrollments.data?.data}
        rowKey={(r) => r.id}
        loading={enrollments.isFetching}
        error={enrollments.error}
        onRetry={() => void enrollments.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={enrollments.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      {/* The reference's exact option list per row was not observed; these are the dialogs it
          names for each list ("View all details", "Enrollment lifecycle", the API log on
          completed orders) plus a way back into the wizard for an order that is not finished. */}
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => pick(setDetails)}>View all details</MenuItem>
        <MenuItem onClick={() => pick(setLifecycle)}>Enrollment lifecycle</MenuItem>
        {menu?.row.status === 'Complete' ? (
          <MenuItem onClick={() => pick(setApiLog)}>API log</MenuItem>
        ) : null}
        {optionsOn ? (
          <MenuItem onClick={() => pick(setChecks)}>USPS address request response</MenuItem>
        ) : null}
        {optionsOn && menu?.row.status === 'Incomplete' && can('PC1172') ? (
          <MenuItem onClick={() => pick(setReminding)}>Send Enrollment Reminder</MenuItem>
        ) : null}
        {menu?.row.status === 'Incomplete' && can('PC1142') ? (
          <MenuItem
            onClick={() => {
              const { row } = menu;
              setMenu(null);
              navigate(stepPath(row.id, currentStep(row)));
            }}
          >
            Continue enrollment
          </MenuItem>
        ) : null}
      </Menu>

      <DetailsDialog target={details} onClose={() => setDetails(null)} />
      <LifecycleDialog target={lifecycle} onClose={() => setLifecycle(null)} />
      <ApiLogDialog target={apiLog} onClose={() => setApiLog(null)} />
      <AddressChecksDialog target={checks} onClose={() => setChecks(null)} />
      <ConfirmDialog
        open={Boolean(reminding)}
        title="Send Enrollment Reminder"
        message={`Send a reminder to finish enrollment ${reminding?.enrollmentId ?? ''}?`}
        confirmLabel="Send Reminder"
        busy={remind.isPending}
        onConfirm={() => reminding && remind.mutate(reminding)}
        onClose={() => setReminding(null)}
      />
    </Box>
  );
}
